import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Menu,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  GridOn as PlotIcon,
  Refresh as RefreshIcon,
  AttachMoney as MoneyIcon,
  Straighten as SizeIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  CheckCircle as ValidIcon,
  Error as ErrorIcon,
  UploadFile as UploadFileIcon,
  VerifiedUser as VerifiedIcon,
} from '@mui/icons-material';

import { getProjects, getPlotsByProject, updatePlot } from '../../services/project';
import { plotService } from '../../services/plot';
import { globalMasterService } from '../../services/stateAPI';
import {
  downloadExcelFile,
  exportAsCSV,
  exportAsExcel,
  exportAsPDF,
  readExcelFile,
} from '../../utils/importExport';
import { bulkImportService } from '../../services/bulkImport';
import { hasReadPermission, hasWritePermission } from '../../utils/menuFilter';
import WarningIcon from '@mui/icons-material/Warning';
import AvatarMui from '@mui/material/Avatar';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import AlertModal from '../../components/AlertModal';
import MoreFilters from '../../components/MoreFilters/MoreFilters';

const sortMapping = {
  amountDesc: { sortBy: 'amount', sortOrder: 'desc' },
  amountAsc: { sortBy: 'amount', sortOrder: 'asc' },
  totalPlotAmountDesc: { sortBy: 'totalPlotAmount', sortOrder: 'desc' },
  totalPlotAmountAsc: { sortBy: 'totalPlotAmount', sortOrder: 'asc' },
  circleRateDesc: { sortBy: 'circleRate', sortOrder: 'desc' },
  circleRateAsc: { sortBy: 'circleRate', sortOrder: 'asc' },
  totalSizeDesc: { sortBy: 'totalSize', sortOrder: 'desc' },
  totalSizeAsc: { sortBy: 'totalSize', sortOrder: 'asc' },
  discountPercentDesc: { sortBy: 'companyDiscountPercent', sortOrder: 'desc' },
  discountPercentAsc: { sortBy: 'companyDiscountPercent', sortOrder: 'asc' },
  totalAuthorityChargesDesc: { sortBy: 'totalAuthorityCharges', sortOrder: 'desc' },
  totalAuthorityChargesAsc: { sortBy: 'totalAuthorityCharges', sortOrder: 'asc' },
};

const PlotListDetails = () => {
  const history = useHistory();
  const location = useLocation();
  const urlProjectId = new URLSearchParams(location.search).get('projectId');

  const canRead = hasReadPermission('project.plot-list') || hasReadPermission('project') || hasReadPermission('plot-list') || hasReadPermission('project.plot-inventory');
  const canWrite = hasWritePermission('project.plot-list') || hasWritePermission('project') || hasWritePermission('plot-list') || hasWritePermission('project.plot-inventory');


  const [plotData, setPlotData] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(urlProjectId || '');
  const [selectedProjectName, setSelectedProjectName] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [searchSite, setSearchSite] = useState('');
  const [searchSector, setSearchSector] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('');
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedPlot, setSelectedPlot] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [saving, setSaving] = useState(false);
  const [alertModal, setAlertModal] = useState({ open: false, title: 'Alert', message: '' });
  const [dialogError, setDialogError] = useState('');

  const [loadingPlots, setLoadingPlots] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [facingOptions, setFacingOptions] = useState([]);
  const [loadingFacing, setLoadingFacing] = useState(false);
  const fileInputRef = useRef(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState('');
  const [importFileName, setImportFileName] = useState('');
  const [parsedImportData, setParsedImportData] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState(null);
  const [authorityOptions, setAuthorityOptions] = useState([]);
  const [loadingAuthority, setLoadingAuthority] = useState(false);
  const [activeImportStep, setActiveImportStep] = useState(0);
  const [validationResult, setValidationResult] = useState(null);
  const [validatedData, setValidatedData] = useState([]);
  // Legacy search/filter states used in getFilteredPlots; keep defaults harmless
  // const [searchQuery, setSearchQuery] = useState('');
  // const [searchSite, setSearchSite] = useState('');
  // const [searchSector, setSearchSector] = useState('');
  // const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoadingProjects(true);
        setError('');
        const data = await getProjects({ projectType: 'Plot' });
        setProjects(data);

        if (urlProjectId) {
          const proj = data.find((p) => p._id === urlProjectId);
          if (proj) {
            setSelectedProjectId(urlProjectId);
            setSelectedProjectName(proj.projectName);
          }
        } else {
          setSelectedProjectId('');
          setSelectedProjectName('Select a Project');
        }
      } catch (err) {
        setError(err.message || 'Failed to load projects');
      } finally {
        setLoadingProjects(false);
      }
    };
    fetchProjects();
  }, [urlProjectId]);

  const fetchPlots = useCallback(async (overrides = {}) => {
    try {
      setLoadingPlots(true);
      setError('');

      if (!selectedProjectId) {
        setPlotData([]);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: rowsPerPage
        });
        return;
      }

      const currentPage = overrides.page !== undefined ? overrides.page : page;
      const currentLimit = overrides.rowsPerPage !== undefined ? overrides.rowsPerPage : rowsPerPage;
      const currentStatus = overrides.statusFilter !== undefined ? overrides.statusFilter : statusFilter;
      const currentSearch = overrides.searchQuery !== undefined ? overrides.searchQuery : searchQuery;
      const currentSortBy = overrides.sortBy !== undefined ? overrides.sortBy : sortBy;

      const apiPage = currentPage + 1;
      const apiLimit = currentLimit;

      const mappedSort = currentSortBy ? (sortMapping[currentSortBy] || {}) : {};

      const response = await getPlotsByProject(
        selectedProjectId,
        'Plot',
        undefined,
        currentStatus !== 'all' ? currentStatus.toLowerCase() : undefined,
        apiPage,
        apiLimit,
        currentSearch || undefined,
        undefined,
        mappedSort.sortBy || undefined,
        mappedSort.sortOrder || undefined
      );

      const inventories = response.inventories || [];

      if (!Array.isArray(inventories)) {
        setPlotData([]);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: apiLimit
        });
        return;
      }

      const project = projects.find((p) => p._id === selectedProjectId);

      const formatted = inventories.map((plot, index) => {
        const plotName = plot.plotName || '';
        const normalizedPlotName = plotName.replace(/^P-/, 'Plot-');
        const plotNo = normalizedPlotName || `Plot-${index + 1}` || '';
        const projectName = plot.projectData?.projectName || project?.projectName || 'Unknown';
        const status = (plot.status || 'select status').toLowerCase();

        return {
          id: plot._id,
          projectId: plot.projectId || selectedProjectId,
          plotNo: plotNo,
          plotName: plotName || plotNo,
          siteName: projectName,
          sector: 'N/A',
          block: 'N/A',
          w1: plot.width || 0,
          l1: plot.length || 0,
          totalSize: plot.totalSize || 0,
          facing: plot.facing || '',
          pricePerSqFt: plot.pricePerSqFt || 0,
          price: plot.pricePerSqFt || 0,
          plcPercentage: plot.plcPercent || 0,
          plcPercent: plot.plcPercent || 0,
          amount: plot.amount || 0,
          guidelineValue: plot.guidelineValue || 0,
          companyDiscountPercent: plot.companyDiscountPercent || 0,
          discountPerSqFt: plot.discountPerSqFt || 0,
          discountAmount: plot.discountAmount || 0,
          status: status,
          remark: plot.remark || '',
          authorityChargesType: plot.authorityChargesType || '',
          authorityChargesPerSqft: plot.authorityChargesPerSqft || 0,
          totalAuthorityCharges: plot.totalAuthorityCharges || 0,
          authority: plot.authority || '',
          plotAmount: plot.plotAmount || 0,
          isEdit: plot.isEdit !== undefined ? plot.isEdit : true,
        };
      });

      setPlotData(formatted);

      if (response.pagination) {
        setPagination(response.pagination);
      }
    } catch (err) {
      setError(err.message || 'Failed to load plots');
      setPlotData([]);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: rowsPerPage
      });
    } finally {
      setLoadingPlots(false);
    }
  }, [selectedProjectId, projects, page, rowsPerPage, statusFilter, searchQuery, sortBy]);

  const handleDownloadSample = () => {
    const sampleData = [
      {
        'Plot Name *': 'A-101',
        'Width (ft) *': '30',
        'Length (ft) *': '50',
        'Facing': 'East',
        'Price per sqft *': '1200',
        'PLC %': '5',
        'Company Discount %': '0',
        'Status': 'Available',
        'Khasara No': '123/4',
        'Remark': 'Premium plot near park'
      }
    ];

    downloadExcelFile({
      rows: sampleData,
      sheetName: 'Plots',
      fileName: 'plot_import_sample.xlsx'
    });
  };

  const validatePlotRow = (row, index) => {
    const errors = [];
    const warnings = [];

    const name = String(row['Plot Name *'] || row['Name'] || row['plotName'] || '').trim();
    const width = Number(row['Width (ft) *'] || row['Width'] || row['width'] || 0);
    const length = Number(row['Length (ft) *'] || row['Length'] || row['length'] || 0);
    const price = Number(row['Price per sqft *'] || row['Price'] || row['pricePerSqFt'] || 0);

    if (!name) errors.push('Plot Name is required');
    if (!width || isNaN(width)) errors.push('Valid Width is required');
    if (!length || isNaN(length)) errors.push('Valid Length is required');
    if (!price || isNaN(price)) errors.push('Valid Price is required');

    return {
      rowIndex: index,
      data: row,
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  };

  const handleImportFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImportFileName(file.name);
    setImportLoading(true);
    setImportError('');
    setImportSuccess('');
    setParsedImportData([]);
    setValidatedData([]);
    setValidationResult(null);

    try {
      const rows = await readExcelFile(file);
      if (!rows || rows.length === 0) {
        throw new Error('No data found in the Excel file');
      }

      const validated = rows.map((row, index) => validatePlotRow(row, index + 1));
      const validCount = validated.filter(v => v.isValid).length;

      setParsedImportData(rows);
      setValidatedData(validated);
      setValidationResult({
        total: validated.length,
        valid: validCount,
        invalid: validated.length - validCount,
      });

      setActiveImportStep(1);
    } catch (err) {
      setImportError(err.message || 'Failed to read Excel file');
    } finally {
      setImportLoading(false);
    }
  };

  const handleImportSubmit = async () => {
    const validRecords = validatedData.filter(v => v.isValid).map(v => v.data);

    if (validRecords.length === 0) {
      setImportError('No valid records to import. Please check for errors.');
      return;
    }

    if (!selectedProjectId) {
      setImportError('Please select a project first.');
      return;
    }

    setImportLoading(true);
    setImportError('');
    setActiveImportStep(2);

    try {
      const formattedData = validRecords.map((row) => {
        const width = Number(row['Width (ft) *'] || row['Width'] || row['width'] || 0);
        const length = Number(row['Length (ft) *'] || row['Length'] || row['length'] || 0);
        const totalSize = width * length;
        const pricePerSqFt = Number(row['Price per sqft *'] || row['Price'] || row['pricePerSqFt'] || 0);
        const plcPercent = Number(row['PLC %'] || row['plcPercent'] || 0);
        const discPercent = Number(row['Company Discount %'] || row['discountPercent'] || 0);

        const dPerSqFt = pricePerSqFt * (discPercent / 100);
        const discountedPrice = pricePerSqFt - dPerSqFt;
        const baseAmount = discountedPrice * totalSize;
        const plcAmount = pricePerSqFt * totalSize * (plcPercent / 100);
        const amount = baseAmount + plcAmount;

        return {
          projectId: selectedProjectId,
          inventoryType: 'Plot',
          plotName: String(row['Plot Name *'] || row['Name'] || row['plotName'] || '').trim(),
          width,
          length,
          totalSize,
          facing: String(row['Facing'] || row['facing'] || '').trim(),
          pricePerSqFt,
          plcPercent,
          companyDiscountPercent: discPercent,
          discountPerSqFt: dPerSqFt,
          discountAmount: dPerSqFt * totalSize,
          amount,
          plotAmount: amount,
          status: String(row['Status'] || row['status'] || 'available').toLowerCase(),
          khasaraNo: String(row['Khasara No'] || row['khasaraNo'] || '').trim(),
          remark: String(row['Remark'] || row['remark'] || '').trim(),
        };
      });

      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const createdBy = user.name || user.email || 'Admin';

      const response = await bulkImportService.uploadFile('inventory', formattedData, createdBy);

      if (response.status || response.success) {
        setImportSuccess(`Successfully imported ${formattedData.length} plots!`);
        setTimeout(() => {
          setImportDialogOpen(false);
          setActiveImportStep(0);
          setImportFileName('');
          setParsedImportData([]);
          setValidatedData([]);
          setValidationResult(null);
          fetchPlots();
        }, 2500);
      } else {
        throw new Error(response.message || 'Import failed');
      }
    } catch (err) {
      setImportError(err.message || 'Failed to import plots');
      setActiveImportStep(1);
    } finally {
      setImportLoading(false);
    }
  };


  useEffect(() => {
    fetchPlots();
  }, [fetchPlots]);

  useEffect(() => {
    const fetchFacingOptions = async () => {
      setLoadingFacing(true);
      try {
        const facingData = await globalMasterService.getFacing();
        setFacingOptions(facingData || []);
      } catch (error) {
        console.error('Failed to fetch facing options', error);
        // Fallback options if API fails
        setFacingOptions([
          { value: 'North', label: 'North' },
          { value: 'South', label: 'South' },
          { value: 'East', label: 'East' },
          { value: 'West', label: 'West' },
          { value: 'North-East', label: 'North-East' },
          { value: 'North-West', label: 'North-West' },
          { value: 'South-East', label: 'South-East' },
          { value: 'South-West', label: 'South-West' },
        ]);
      } finally {
        setLoadingFacing(false);
      }
    };

    fetchFacingOptions();
  }, []);

  // Calculation logic moved to handleFieldChange for better control and performance

  const showAlert = (message, title = 'Alert') => {
    setAlertModal({ open: true, title, message });
  };

  const showSuccess = (msg) => {
    showAlert(msg, 'Success');
  };

  useEffect(() => {
    const fetchAuthorityOptions = async () => {
      setLoadingAuthority(true);
      try {
        const API_BASE = process.env.REACT_APP_API_BASE_URL;
        const response = await fetch(
          `${API_BASE}/api/globalMasterDetails?type=authority_charges`,
        );

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const result = await response.json();
        const data = Array.isArray(result.data)
          ? result.data
            .filter(item => item.status === true) // Only active authorities
            .map((item) => ({
              value: item._id,
              label: item.details?.authorityName || 'Unknown Authority',
              perSqFtCharge: item.details?.perSqFtCharge || 0,
            }))
          : [];

        setAuthorityOptions(data);
      } catch (error) {
        console.error('Failed to fetch authority options', error);
        // Fallback options if API fails
        setAuthorityOptions([
          { value: 'local_authority_1', label: 'Local Municipal Authority', perSqFtCharge: 50 },
          { value: 'state_authority_1', label: 'State Housing Authority', perSqFtCharge: 75 },
          { value: 'central_authority_1', label: 'Central Government Authority', perSqFtCharge: 100 },
        ]);
      } finally {
        setLoadingAuthority(false);
      }
    };

    fetchAuthorityOptions();
  }, []);

  const handleAuthoritySelect = (authorityId) => {
    const selected = authorityOptions.find(a => a.value === authorityId);
    setEditedData(prev => {
      const perSq = selected ? Number(selected.perSqFtCharge || 0) : 0;
      const totalAuth = perSq * Number(prev.totalSize || 0); // Assuming totalSize is available in editedData
      const currentAmount = Number(prev.amount || 0);

      return {
        ...prev,
        authority: authorityId,
        authorityChargesType: selected ? selected.label : '',
        authorityChargesPerSqft: perSq,
        totalAuthorityCharges: Number(totalAuth.toFixed(2)),
        plotAmount: (currentAmount + totalAuth).toFixed(2),
      };
    });
  };

  const getFilteredPlots = () => {
    return plotData.filter((plot) => {
      const matchesSearch = searchQuery
        ? (plot.plotName?.toLowerCase() || '').includes(searchQuery.toLowerCase())
        : true;
      const matchesSite = searchSite ? plot.siteName === searchSite : true;
      const matchesSector = searchSector ? plot.sector === searchSector : true;
      return matchesSearch && matchesSite && matchesSector;
    });
  };

  const getStatusStyle = (status) => {
    const normalizedStatus = (status || '').toLowerCase();
    const styles = {
      available: { backgroundColor: 'rgba(76, 175, 80, 0.12)', color: '#2e7d32' },
      booked: { backgroundColor: 'rgba(25, 118, 210, 0.12)', color: '#1976d2' },
      'under construction': { backgroundColor: 'rgba(255, 193, 7, 0.12)', color: '#f57c00' },
      hold: { backgroundColor: 'rgba(244, 67, 54, 0.12)', color: '#d32f2f' },
    };
    return styles[normalizedStatus] || { backgroundColor: 'rgba(158, 158, 158, 0.12)', color: '#616161' };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const handleView = (plot) => {
    setSelectedPlot(plot);
    setViewDialogOpen(true);
  };

  const handleEdit = (plot) => {
    // Resolve authority ID from label if missing
    let authorityId = plot.authority || '';
    if (authorityOptions.length > 0) {
      const idExists = authorityOptions.some(a => a.value === authorityId);
      if (!idExists || !authorityId) {
        const targetLabel = (plot.authorityChargesType || plot.authority || '')
          .toString().trim().toLowerCase();
        if (targetLabel) {
          const found = authorityOptions.find(a =>
            a.label?.toString().trim().toLowerCase() === targetLabel
          );
          if (found) authorityId = found.value;
        }
      }
    }

    const pricePerSqFt = Number(plot.pricePerSqFt || plot.price || 0);
    const companyDiscountPercent = Number(plot.companyDiscountPercent || 0);
    const width = Number(plot.w1 || 0);
    const length = Number(plot.l1 || 0);
    const totalSize = plot.totalSize ? Number(plot.totalSize) : (width * length);

    const calculatedDPerSqFt = pricePerSqFt * (companyDiscountPercent / 100);
    const calculatedDAmount = calculatedDPerSqFt * totalSize;

    const plcPercentValue = Number(plot.plcPercentage || plot.plcPercent || 0);
    const calculatedPlcAmount = pricePerSqFt * totalSize * (plcPercentValue / 100);

    setSelectedPlot(plot);
    setEditedData({
      ...plot,
      projectId: plot.projectId || selectedProjectId,
      width: plot.w1 || '',
      length: plot.l1 || '',
      pricePerSqFt: plot.pricePerSqFt || plot.price || '',
      plcPercent: plot.plcPercentage || plot.plcPercent || '',
      plcAmount: calculatedPlcAmount.toFixed(2),
      companyDiscountPercent: plot.companyDiscountPercent || '',
      discountPerSqFt: calculatedDPerSqFt.toFixed(2),
      discountAmount: calculatedDAmount.toFixed(2),
      authorityChargesType: plot.authorityChargesType || '',
      authorityChargesPerSqft: plot.authorityChargesPerSqft || '',
      totalAuthorityCharges: plot.totalAuthorityCharges || 0,
      authority: authorityId,
      plotAmount: plot.plotAmount || 0,
      amount: plot.amount || 0,
    });
    setEditDialogOpen(true);
  };

  // Sync authority ID if options load after dialog is opened
  useEffect(() => {
    if (editDialogOpen && editedData.authorityChargesType && authorityOptions.length > 0) {
      const idExists = authorityOptions.some(a => a.value === editedData.authority);
      if (!idExists) {
        const targetLabel = editedData.authorityChargesType.toString().trim().toLowerCase();
        const found = authorityOptions.find(a =>
          a.label?.toString().trim().toLowerCase() === targetLabel
        );
        if (found) {
          setEditedData(prev => ({ ...prev, authority: found.value }));
        }
      }
    }
  }, [editDialogOpen, authorityOptions, editedData.authority, editedData.authorityChargesType]);

  const handleSave = async () => {
    if (!selectedPlot) return;

    // Frontend validation — block before hitting the API
    const currentStatus = (editedData.status || '').toLowerCase().trim();
    const invalidStatuses = ['', 'select status'];
    if (invalidStatuses.includes(currentStatus)) {
      setDialogError('Please select a valid status before saving.');
      return;
    }

    setSaving(true);
    setDialogError('');

    try {
      const payload = {
        projectId: editedData.projectId || selectedProjectId,
        inventoryType: 'Plot',
        plotName: editedData.plotName || '',
        width: Number(editedData.width) || 0,
        length: Number(editedData.length) || 0,
        totalSize: Number(editedData.totalSize) || 0,
        facing: editedData.facing || '',
        pricePerSqFt: Number(editedData.pricePerSqFt) || 0,
        plcPercent: Number(editedData.plcPercent) || 0,
        amount: Number(editedData.amount) || 0,
        guidelineValue: Number(editedData.guidelineValue) || 0,
        companyDiscountPercent: Number(editedData.companyDiscountPercent) || 0,
        discountPerSqFt: Number(editedData.discountPerSqFt) || 0,
        discountAmount: Number(editedData.discountAmount) || 0,
        status: (editedData.status || 'available').toLowerCase(),
        authority: editedData.authority || '',
        authorityChargesType: editedData.authorityChargesType || '',
        authorityChargesPerSqft: Number(editedData.authorityChargesPerSqft) || 0,
        totalAuthorityCharges: Number(editedData.totalAuthorityCharges) || 0,
        plotAmount: Number(editedData.plotAmount) || 0,
        remark: editedData.remark || '',
      };

      await updatePlot(selectedPlot.id, payload);

      await fetchPlots();

      setEditDialogOpen(false);
      setSelectedPlot(null);
      setEditedData({});
      setDialogError('');
      showSuccess('Plot updated successfully!');
    } catch (err) {
      setDialogError(err.message || 'Failed to update plot');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseEdit = () => {
    setEditDialogOpen(false);
    setSelectedPlot(null);
    setEditedData({});
    setDialogError('');
  };

  const handleCloseView = () => {
    setViewDialogOpen(false);
    setSelectedPlot(null);
  };

  const handleFieldChange = (field, value) => {
    setEditedData((prev) => {
      const updated = { ...prev, [field]: value };

      if (['width', 'length', 'pricePerSqFt', 'companyDiscountPercent', 'plcPercent', 'plcAmount', 'authorityChargesPerSqft', 'discountPerSqFt', 'discountAmount'].includes(field)) {
        const width = Number(updated.width || 0);
        const length = Number(updated.length || 0);
        const totalSize = width * length;
        const originalPricePerSqFt = Number(updated.pricePerSqFt || 0);
        const authorityChargesPerSqft = Number(updated.authorityChargesPerSqft || 0);

        let plcPercentValue = Number(updated.plcPercent || 0);
        let plcAmountValue = Number(updated.plcAmount || 0);

        if (field === 'plcPercent') {
          plcPercentValue = Number(value);
          plcAmountValue = originalPricePerSqFt * totalSize * (plcPercentValue / 100);
        } else if (field === 'plcAmount') {
          plcAmountValue = Number(value);
          const baseTotal = originalPricePerSqFt * totalSize;
          plcPercentValue = baseTotal > 0 ? (plcAmountValue / baseTotal) * 100 : 0;
        } else {
          // Triggered by other fields (width, length, pricePerSqFt)
          plcAmountValue = originalPricePerSqFt * totalSize * (plcPercentValue / 100);
        }

        let dPerSqFt = Number(updated.discountPerSqFt || 0);
        let dAmount = Number(updated.discountAmount || 0);
        let discountPercent = Number(updated.companyDiscountPercent || 0);

        if (field === 'companyDiscountPercent') {
          dPerSqFt = originalPricePerSqFt * (discountPercent / 100);
          dAmount = dPerSqFt * totalSize;
        } else if (field === 'discountPerSqFt') {
          dPerSqFt = Number(value);
          dAmount = dPerSqFt * totalSize;
          discountPercent = originalPricePerSqFt > 0 ? (dPerSqFt / originalPricePerSqFt) * 100 : 0;
        } else if (field === 'discountAmount') {
          dAmount = Number(value);
          dPerSqFt = totalSize > 0 ? dAmount / totalSize : 0;
          discountPercent = originalPricePerSqFt > 0 ? (dPerSqFt / originalPricePerSqFt) * 100 : 0;
        } else {
          // Triggered by width, length, or pricePerSqFt
          dPerSqFt = originalPricePerSqFt * (discountPercent / 100);
          dAmount = dPerSqFt * totalSize;
        }

        const discountedPricePerSqFt = originalPricePerSqFt - dPerSqFt;
        const baseAmount = totalSize * discountedPricePerSqFt;
        const plcAmount = plcAmountValue;
        const totalAuthorityCharges = totalSize * authorityChargesPerSqft;
        const amount = baseAmount + plcAmount;

        return {
          ...updated,
          totalSize: isNaN(totalSize) ? 0 : totalSize,
          companyDiscountPercent: field === 'companyDiscountPercent' ? value : (isNaN(discountPercent) ? 0 : discountPercent.toFixed(2)),
          discountPerSqFt: field === 'discountPerSqFt' ? value : (isNaN(dPerSqFt) ? 0 : dPerSqFt.toFixed(2)),
          discountAmount: field === 'discountAmount' ? value : (isNaN(dAmount) ? 0 : dAmount.toFixed(2)),
          plcPercent: field === 'plcPercent' ? value : (isNaN(plcPercentValue) ? 0 : plcPercentValue.toFixed(2)),
          plcAmount: field === 'plcAmount' ? value : (isNaN(plcAmountValue) ? 0 : plcAmountValue.toFixed(2)),
          amount: isNaN(amount) ? 0 : amount.toFixed(2),
          totalAuthorityCharges: isNaN(totalAuthorityCharges) ? 0 : totalAuthorityCharges.toFixed(2),
          plotAmount: isNaN(amount + totalAuthorityCharges) ? 0 : (amount + totalAuthorityCharges).toFixed(2),
        };
      }

      return updated;
    });
  };

  const handleAddNew = () => {
    if (selectedProjectId) {
      history.push(`/app/project/plot/add?projectId=${selectedProjectId}`);
    } else {
      history.push('/app/project/plot/add');
    }
  };

  const handleDelete = async (id) => {
    console.log("id----------", id)
    if (!window.confirm('Are you sure you want to delete this plot?')) {
      return;
    }

    try {
      setSaving(true);
      setError('');

      await updatePlot(id, {
        projectId: selectedProjectId || undefined,
        inventoryType: 'Plot',
        isDeleted: true,
      });

      setPlotData((prev) => prev.filter((p) => p.id !== id));
      showSuccess('Plot deleted successfully!');
    } catch (err) {
      setError(err.message || 'Failed to delete plot');
    } finally {
      setSaving(false);
    }
  };

  const handleProjectChange = (projectId) => {
    setSelectedProjectId(projectId);
    const proj = projects.find((p) => p._id === projectId);
    setSelectedProjectName(proj?.projectName || '');
    setPage(0);
    history.replace(`?projectId=${projectId}`);
  };

  const handleReset = () => {
    setSelectedProjectId('');
    setSelectedProjectName('Select a Project');
    setSearchQuery('');
    setSearchSite('');
    setSearchSector('');
    setStatusFilter('all');
    setSortBy('');
    setPage(0);
    history.replace(location.pathname);
  };

  const handleOpenExportMenu = (event) => {
    setExportMenuAnchorEl(event.currentTarget);
  };

  const handleCloseExportMenu = () => {
    setExportMenuAnchorEl(null);
  };

  const getExportData = () => {
    return getFilteredPlots().map((plot, index) => ({
      'S.No': index + 1,
      'Plot ID': plot.plotNo || plot.plotName || '-',
      'Dimensions': `${plot.w1 || 0}x${plot.l1 || 0}`,
      'Area': `${plot.totalSize} sq.ft`,
      'Facing': plot.facing || '-',
      'Price/ft': plot.pricePerSqFt || 0,
      'PLC%': `${plot.plcPercent || 0}%`,
      'Disc%': `${plot.companyDiscountPercent || 0}%`,
      'Status': plot.status,
      'Total Amount': (plot.amount || 0) + (plot.totalAuthorityCharges || 0)
    }));
  };

  const handleExportExcel = () => {
    const data = getExportData();
    exportAsExcel({ rows: data, fileNameBase: 'plot-list', sheetName: 'Plots' });
    setExportMenuAnchorEl(null);
  };

  const handleExportCSV = () => {
    const data = getExportData();
    exportAsCSV({ rows: data, fileNameBase: 'plot-list' });
    setExportMenuAnchorEl(null);
  };

  const handleExportPDF = () => {
    const data = getExportData();
    exportAsPDF({
      rows: data,
      fileNameBase: 'plot-list',
      title: `${selectedProjectName} - Plot List`
    });
    setExportMenuAnchorEl(null);
  };

  const filteredData = getFilteredPlots();

  if (!canRead) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh', p: 3 }}>
        <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, textAlign: 'center', maxWidth: 500, borderRadius: 3, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
          <AvatarMui sx={{ bgcolor: '#fee2e2', color: '#ef4444', width: 80, height: 80, mb: 3, mx: 'auto' }}>
            <WarningIcon sx={{ fontSize: 50 }} />
          </AvatarMui>
          <Typography variant="h4" fontWeight="bold" color="#1a202c" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
            You do not have permission to view Plot List Details. Please contact your administrator for access.
          </Typography>
          <Button
            variant="contained"
            onClick={() => history.push('/app/dashboard')}
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3d5afe' },
              px: 4, py: 1.2, borderRadius: 2, fontWeight: 600, textTransform: 'none'
            }}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, mr: 5 }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: 2 }}>
        <Box>
          <Typography variant='h4' sx={{ fontWeight: 700, color: '#000' }}>
            Plot Management
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Track plots and inventory for each project
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center', flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant='outlined'
            sx={{ textTransform: 'none', fontWeight: 600, flex: { xs: 1, sm: 'none' } }}
            onClick={handleReset}
            startIcon={<RefreshIcon />}
          >
            Reset
          </Button>
          {canWrite && (
            <Button
              variant='outlined'
              onClick={handleOpenExportMenu}
              sx={{ textTransform: 'none', fontWeight: 600, flex: { xs: 1, sm: 'none' } }}
            >
              Export
            </Button>
          )}
          <Menu
            anchorEl={exportMenuAnchorEl}
            open={Boolean(exportMenuAnchorEl)}
            onClose={handleCloseExportMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={handleExportExcel}>Export as Excel</MenuItem>
            <MenuItem onClick={handleExportCSV}>Export as CSV</MenuItem>
            <MenuItem onClick={handleExportPDF}>Export as PDF</MenuItem>
          </Menu>
          {selectedProjectId && canWrite && (
            <>
              <Button
                variant='outlined'
                color='primary'
                startIcon={<CloudUploadIcon />}
                onClick={() => setImportDialogOpen(true)}
                sx={{ textTransform: 'none', fontWeight: 600, flex: { xs: 1, sm: 'none' } }}
              >
                Bulk Import
              </Button>
              <Button
                variant='contained'
                color='primary'
                startIcon={<AddIcon />}
                onClick={handleAddNew}
                sx={{ textTransform: 'none', fontWeight: 600, flex: { xs: 1, sm: 'none' } }}
              >
                Add New Plot
              </Button>
            </>
          )}
          <RefreshButton
            onClick={() => fetchPlots()}
            loading={loadingPlots}
            disabled={!selectedProjectId}
            sx={{
              height: { xs: 'auto', sm: 40 },
              px: 2,
              flex: { xs: 1, sm: 'none' }
            }}
          />
        </Box>
      </Box>


      {error && (
        <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity='success' sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={12} md={selectedProjectId ? 3 : 6}>
              <FormControl fullWidth>
                <InputLabel>Project</InputLabel>
                <Select
                  value={selectedProjectId}
                  onChange={(e) => handleProjectChange(e.target.value)}
                  label='Project'
                >
                  <MenuItem value=''>Select Project</MenuItem>
                  {projects.map((project) => (
                    <MenuItem key={project._id} value={project._id}>
                      {project.projectName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={selectedProjectId ? 3 : 6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                  label='Status'
                >
                  <MenuItem value='all'>All Status</MenuItem>
                  <MenuItem value='available'>Available</MenuItem>
                  <MenuItem value='booked'>Booked</MenuItem>
                  <MenuItem value='hold'>Hold</MenuItem>
                  <MenuItem value='sold'>Under Construction</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {selectedProjectId && (
              <>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label='Search Plot'
                    variant='outlined'
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setPage(0);
                    }}
                    placeholder='Search by plot name...'
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <SearchIcon color='action' />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <MoreFilters
                    expanded={moreFiltersOpen}
                    onToggle={() => setMoreFiltersOpen(!moreFiltersOpen)}
                    sortBy={sortBy}
                    onChangeSortBy={setSortBy}
                    onApply={fetchPlots}
                    onReset={handleReset}
                    type="plotInventory"
                  />
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
      </Card>

      {loadingPlots && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading plots...</Typography>
        </Paper>
      )}

      {!loadingPlots && (
        <>
          {selectedProjectId && filteredData.length > 0 ? (
            <Paper sx={{ borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>S. No</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Plot ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Plot Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>W1 (ft)</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>L1 (ft)</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Total Size (sq.ft)</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Facing</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Price/sq.ft</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Discount%</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>PLC%</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Authority Charges (Type | Rate sq.ft)</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Total Authority</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Total Plot Amount</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Circle Rate </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Remark</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }} align='center'>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredData.map((plot, i) => (
                      <TableRow
                        key={plot.id}
                        hover={plot.isEdit}
                        sx={{
                          opacity: plot.isEdit ? 1 : 0.5,
                          filter: plot.isEdit ? 'none' : 'blur(1px)',
                          pointerEvents: plot.isEdit ? 'auto' : 'none',
                          backgroundColor: plot.isEdit ? 'transparent' : '#f5f5f5',
                        }}
                      >
                        <TableCell><Typography fontWeight='medium' color='primary' variant='body2'>{i + 1 + page * rowsPerPage}</Typography></TableCell>
                        <TableCell><Typography fontWeight='medium' color='primary' variant='body2'>{plot.plotNo || '-'}</Typography></TableCell>
                        <TableCell><Typography variant='body2' fontWeight='500'>{plot.plotName}</Typography></TableCell>
                        <TableCell><Typography variant='body2'>{plot.w1 ? `${plot.w1} ft` : '-'}</Typography></TableCell>
                        <TableCell><Typography variant='body2'>{plot.l1 ? `${plot.l1} ft` : '-'}</Typography></TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <SizeIcon sx={{ mr: 0.5, fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant='body2' fontWeight='500'>{plot.totalSize} sq.ft</Typography>
                          </Box>
                        </TableCell>
                        <TableCell><Chip label={plot.facing || '-'} size='small' variant='outlined' color='info' /></TableCell>
                        <TableCell><Typography variant='body2' color='text.secondary'>{plot.pricePerSqFt ? `₹${plot.pricePerSqFt.toFixed(2)}/sq.ft` : '-'}</Typography></TableCell>
                        <TableCell><Chip label={`${plot.companyDiscountPercent || 0}%`} size='small' sx={{ backgroundColor: '#e3f2fd', color: '#1976d2', fontWeight: 600 }} /></TableCell>
                        <TableCell><Chip label={`${plot.plcPercentage || 0}%`} size='small' sx={{ backgroundColor: '#e3f2fd', color: '#1976d2', fontWeight: 600 }} /></TableCell>
                        <TableCell>
                          <Typography variant='body2'>
                            {plot.authorityChargesType || '-'}
                            {plot.authorityChargesType && plot.authorityChargesPerSqft ? ' | ' : ''}
                            {plot.authorityChargesPerSqft ? `₹${plot.authorityChargesPerSqft}` : ''}
                          </Typography>
                        </TableCell>
                        <TableCell><Typography variant='body2'>{formatCurrency(plot.totalAuthorityCharges)}</Typography></TableCell>

                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant='body2' fontWeight='medium' color='success.main'>
                              {formatCurrency(plot.amount)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant='body2' fontWeight='bold' color='success.dark'>
                            {formatCurrency((plot.amount || 0) + (plot.totalAuthorityCharges || 0))}
                          </Typography>
                        </TableCell>
                        <TableCell><Typography variant='body2' color='text.secondary'>{formatCurrency(plot.guidelineValue)}</Typography></TableCell>
                        <TableCell><Chip label={plot.status} size='small' style={getStatusStyle(plot.status)} /></TableCell>
                        <TableCell><Typography variant='body2' color='text.secondary' sx={{ maxWidth: 200 }}>{plot.remark}</Typography></TableCell>
                        <TableCell align='center'>
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Tooltip title={plot.isEdit ? 'View Plot' : 'Plot is not editable'}>
                              <span>
                                <IconButton
                                  color='info'
                                  size='small'
                                  onClick={() => handleView(plot)}
                                  disabled={!plot.isEdit}
                                >
                                  <VisibilityIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                            {canWrite ? (
                              <>
                                <Tooltip title={plot.isEdit ? 'Edit Plot' : 'Plot is not editable'}>
                                  <span>
                                    <IconButton
                                      color='primary'
                                      size='small'
                                      onClick={() => handleEdit(plot)}
                                      disabled={!plot.isEdit}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title={plot.isEdit ? 'Delete Plot' : 'Plot is not editable'}>
                                  <span>
                                    <IconButton
                                      color='error'
                                      size='small'
                                      onClick={() => handleDelete(plot.id)}
                                      disabled={!plot.isEdit}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </>
                            ) : (
                              <></>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination

                component='div'
                count={pagination.totalItems || 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => {
                  setPage(newPage);
                }}
                onRowsPerPageChange={(e) => {
                  const newRowsPerPage = parseInt(e.target.value, 10);
                  setRowsPerPage(newRowsPerPage);
                  setPage(0);
                }}
              />
            </Paper>
          ) : (
            <Card
              sx={{
                p: 10,
                textAlign: 'center',
                borderRadius: 2,
                border: '1px dashed',
                borderColor: 'divider',
                backgroundColor: 'white',
              }}
            >
              <Typography variant='h6' sx={{ fontWeight: 700, color: 'text.secondary' }}>
                {selectedProjectId ? 'No Plots Found' : 'No Project Selected'}
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                {selectedProjectId
                  ? 'No plots match your current status filter for this project.'
                  : 'Please select a project from the dropdown above to view plot inventory.'}
              </Typography>
              {!selectedProjectId && (
                <Typography variant='caption' color='text.disabled' sx={{ mt: 2, display: 'block' }}>
                  Select a project to see available plots, pricing, and status.
                </Typography>
              )}
            </Card>
          )}
        </>
      )}

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseView} maxWidth='md' fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography fontWeight='bold'>Plot Details - {selectedPlot?.plotNo}</Typography>
          <IconButton onClick={handleCloseView} size='small'><CloseIcon /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          {selectedPlot && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label='Plot Number' value={selectedPlot.plotNo || ''} disabled InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label='Plot Name' value={selectedPlot.plotName || ''} disabled InputProps={{ readOnly: true }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField fullWidth type='number' label='W1 (feet)' value={selectedPlot.w1 || ''} disabled InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth type='number' label='L1 (feet)' value={selectedPlot.l1 || ''} disabled InputProps={{ readOnly: true }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField fullWidth label='Total Size (sq.ft)' value={selectedPlot.totalSize || ''} disabled InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label='Facing' value={selectedPlot.facing || ''} disabled InputProps={{ readOnly: true }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type='number'
                  label='Price per sq.ft'
                  value={selectedPlot.pricePerSqFt || selectedPlot.price || ''}
                  disabled
                  InputProps={{ readOnly: true, startAdornment: <InputAdornment position='start'>₹</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type='number'
                  label='Discount Percentage'
                  value={selectedPlot.companyDiscountPercent || ''}
                  disabled
                  InputProps={{ readOnly: true, endAdornment: <InputAdornment position='end'>%</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type='number'
                  label='Discount per sq.ft'
                  value={selectedPlot.discountPerSqFt || 0}
                  disabled
                  InputProps={{ readOnly: true, startAdornment: <InputAdornment position='start'>₹</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type='number'
                  label='Discount Amount'
                  value={selectedPlot.discountAmount || 0}
                  disabled
                  InputProps={{ readOnly: true, startAdornment: <InputAdornment position='start'>₹</InputAdornment> }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type='number'
                  label='PLC Percentage'
                  value={selectedPlot.plcPercentage || ''}
                  disabled
                  InputProps={{ readOnly: true, endAdornment: <InputAdornment position='end'>%</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type='number'
                  label='PLC Amount'
                  value={
                    selectedPlot.pricePerSqFt && selectedPlot.totalSize
                      ? (Number(selectedPlot.pricePerSqFt) * Number(selectedPlot.totalSize) * (Number(selectedPlot.plcPercentage || 0) / 100)).toFixed(2)
                      : ''
                  }
                  disabled
                  InputProps={{ readOnly: true, startAdornment: <InputAdornment position='start'>₹</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label='Authority Charges Type' value={selectedPlot.authorityChargesType || ''} disabled InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label='Authority Charges per sq.ft' value={selectedPlot.authorityChargesPerSqft || ''} disabled InputProps={{ readOnly: true, startAdornment: <InputAdornment position='start'>₹</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label='Total Authority Charges' value={formatCurrency(selectedPlot.totalAuthorityCharges) || ''} disabled InputProps={{ readOnly: true }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label='Amount'
                  value={formatCurrency(selectedPlot.amount) || ''}
                  disabled
                  InputProps={{ readOnly: true, startAdornment: <InputAdornment position='start'>₹</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label='Circle Rate (₹)'
                  value={formatCurrency(selectedPlot.guidelineValue) || ''}
                  disabled
                  InputProps={{ readOnly: true, startAdornment: <InputAdornment position='start'>₹</InputAdornment> }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField fullWidth label='Status' value={selectedPlot.status || ''} disabled InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label='Site Name' value={selectedPlot.siteName || ''} disabled InputProps={{ readOnly: true }} />
              </Grid>
              {/* <Grid item xs={12} sm={6}>
                <TextField fullWidth label='Sector' value={selectedPlot.sector || ''} disabled InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label='Block' value={selectedPlot.block || ''} disabled InputProps={{ readOnly: true }} />
              </Grid> */}

              <Grid item xs={12}>
                <TextField fullWidth multiline rows={3} label='Remark' value={selectedPlot.remark || ''} disabled InputProps={{ readOnly: true }} />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseView} variant='outlined'>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEdit} maxWidth='md' fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography fontWeight='bold'>Edit Plot - {selectedPlot?.plotNo}</Typography>
          <IconButton onClick={handleCloseEdit} size='small'><CloseIcon /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {dialogError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setDialogError('')}
            >
              {dialogError}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Project</InputLabel>
                <Select
                  value={editedData.projectId || selectedProjectId || ''}
                  onChange={(e) => handleFieldChange('projectId', e.target.value)}
                  label='Project'
                  disabled
                >
                  {projects.map((proj) => (
                    <MenuItem key={proj._id} value={proj._id}>
                      {proj.projectName} ({proj?.projectId || proj?.projectNo || ''})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label='Plot Name'
                value={editedData.plotName || ''}
                onChange={(e) => handleFieldChange('plotName', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type='number'
                label='Width (feet)'
                value={editedData.width || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || (!isNaN(val) && parseFloat(val) >= 0)) {
                    handleFieldChange('width', val);
                  }
                }}
                onKeyDown={(e) => {
                  if (['-', 'e', 'E', '+', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                sx={{
                  '& input[type=number]': {
                    MozAppearance: 'textfield',
                  },
                  '& input[type=number]::-webkit-outer-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '& input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type='number'
                label='Length (feet)'
                value={editedData.length || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || (!isNaN(val) && parseFloat(val) >= 0)) {
                    handleFieldChange('length', val);
                  }
                }}
                onKeyDown={(e) => {
                  if (['-', 'e', 'E', '+', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                sx={{
                  '& input[type=number]': {
                    MozAppearance: 'textfield',
                  },
                  '& input[type=number]::-webkit-outer-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '& input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label='Total Size (sq.ft)'
                value={editedData.totalSize || ''}
                disabled
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Facing</InputLabel>
                <Select
                  value={editedData.facing || ''}
                  onChange={(e) => handleFieldChange('facing', e.target.value)}
                  label='Facing'
                  disabled={loadingFacing}
                >
                  <MenuItem value="">
                    {loadingFacing ? 'Loading...' : 'Select Facing'}
                  </MenuItem>
                  {facingOptions.map((option) => (
                    <MenuItem key={option.id} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type='number'
                label='Price per sq.ft'
                value={editedData.pricePerSqFt || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || (!isNaN(val) && parseFloat(val) >= 0)) {
                    handleFieldChange('pricePerSqFt', val);
                  }
                }}
                onKeyDown={(e) => {
                  if (['-', 'e', 'E', '+', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                InputProps={{
                  startAdornment: <InputAdornment position='start'>₹</InputAdornment>
                }}
                sx={{
                  '& input[type=number]': {
                    MozAppearance: 'textfield',
                  },
                  '& input[type=number]::-webkit-outer-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '& input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type='number'
                label='Discount Percentage'
                value={editedData.companyDiscountPercent || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || (!isNaN(val) && parseFloat(val) >= 0)) {
                    handleFieldChange('companyDiscountPercent', val);
                  }
                }}
                onKeyDown={(e) => {
                  if (['-', 'e', 'E', '+', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                InputProps={{
                  endAdornment: <InputAdornment position='end'>%</InputAdornment>
                }}
                sx={{
                  '& input[type=number]': {
                    MozAppearance: 'textfield',
                  },
                  '& input[type=number]::-webkit-outer-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '& input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type='number'
                label='Discount per sq.ft'
                value={editedData.discountPerSqFt || 0}
                onChange={(e) => handleFieldChange('discountPerSqFt', e.target.value)}
                onKeyDown={(e) => {
                  if (['-', 'e', 'E', '+', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                InputProps={{
                  startAdornment: <InputAdornment position='start'>₹</InputAdornment>
                }}
                sx={{
                  '& input[type=number]': {
                    MozAppearance: 'textfield',
                  },
                  '& input[type=number]::-webkit-outer-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '& input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type='number'
                label='Discount Amount'
                value={editedData.discountAmount || 0}
                onChange={(e) => handleFieldChange('discountAmount', e.target.value)}
                onKeyDown={(e) => {
                  if (['-', 'e', 'E', '+', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                InputProps={{
                  startAdornment: <InputAdornment position='start'>₹</InputAdornment>
                }}
                sx={{
                  '& input[type=number]': {
                    MozAppearance: 'textfield',
                  },
                  '& input[type=number]::-webkit-outer-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '& input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type='number'
                label='PLC Percentage'
                value={editedData.plcPercent || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || (!isNaN(val) && parseFloat(val) >= 0)) {
                    handleFieldChange('plcPercent', val);
                  }
                }}
                onKeyDown={(e) => {
                  if (['-', 'e', 'E', '+', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                InputProps={{
                  endAdornment: <InputAdornment position='end'>%</InputAdornment>
                }}
                sx={{
                  '& input[type=number]': {
                    MozAppearance: 'textfield',
                  },
                  '& input[type=number]::-webkit-outer-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '& input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type='number'
                label='PLC Amount'
                value={editedData.plcAmount !== undefined && editedData.plcAmount !== null && editedData.plcAmount !== '' ? editedData.plcAmount : ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || (!isNaN(val) && parseFloat(val) >= 0)) {
                    handleFieldChange('plcAmount', val);
                  }
                }}
                onKeyDown={(e) => {
                  if (['-', 'e', 'E', '+', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                InputProps={{
                  startAdornment: <InputAdornment position='start'>₹</InputAdornment>
                }}
                sx={{
                  '& input[type=number]': {
                    MozAppearance: 'textfield',
                  },
                  '& input[type=number]::-webkit-outer-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '& input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Authority</InputLabel>
                <Select
                  value={editedData.authority || ''}
                  onChange={(e) => handleAuthoritySelect(e.target.value)}
                  label="Authority"
                  disabled={loadingAuthority}
                >
                  <MenuItem value="">
                    <em>Select Authority</em>
                  </MenuItem>
                  {authorityOptions.map((authority) => (
                    <MenuItem key={authority.value} value={authority.value}>
                      {authority.label} (₹{authority.perSqFtCharge}/sq.ft)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label='Authority Charges Type'
                value={editedData.authorityChargesType || ''}
                onChange={(e) => handleFieldChange('authorityChargesType', e.target.value)}
              />
            </Grid> */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type='number'
                label='Authority Charges per sq.ft'
                value={editedData.authorityChargesPerSqft || ''}
                disabled
                InputProps={{
                  startAdornment: <InputAdornment position='start'>₹</InputAdornment>,
                  readOnly: true
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type='number'
                label='Total Authority Charges'
                value={editedData.totalAuthorityCharges || ''}
                disabled
                InputProps={{
                  startAdornment: <InputAdornment position='start'>₹</InputAdornment>,
                  readOnly: true
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label='Amount'
                value={editedData.amount || 0}
                disabled
                InputProps={{
                  startAdornment: <InputAdornment position='start'>₹</InputAdornment>,
                  readOnly: true
                }}
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                    fontWeight: 600,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type='number'
                label='Circle Rate'
                value={editedData.guidelineValue || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || (!isNaN(val) && parseFloat(val) >= 0)) {
                    handleFieldChange('guidelineValue', val);
                  }
                }}
                onKeyDown={(e) => {
                  if (['-', 'e', 'E', '+'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                InputProps={{
                  startAdornment: <InputAdornment position='start'>₹</InputAdornment>
                }}
                sx={{
                  '& input[type=number]': {
                    MozAppearance: 'textfield',
                  },
                  '& input[type=number]::-webkit-outer-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '& input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                error={
                  !!dialogError &&
                  ['', 'select status'].includes((editedData.status || '').toLowerCase().trim())
                }
              >
                <InputLabel id="edit-plot-status-label" required shrink>Status</InputLabel>
                <Select
                  labelId="edit-plot-status-label"
                  label="Status *"
                  value={
                    ['', 'select status'].includes((editedData.status || '').toLowerCase())
                      ? ''
                      : editedData.status || ''
                  }
                  onChange={(e) => {
                    handleFieldChange('status', e.target.value);
                    if (dialogError) setDialogError('');
                  }}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    <em>Select Status</em>
                  </MenuItem>
                  <MenuItem value='available'>Available</MenuItem>
                  <MenuItem value='booked'>Booked</MenuItem>
                  <MenuItem value='hold'>Hold</MenuItem>
                  <MenuItem value='UnderContruction'>Under Construction</MenuItem>
                </Select>
                {!!dialogError &&
                  ['', 'select status'].includes((editedData.status || '').toLowerCase().trim()) && (
                    <Typography variant='caption' color='error' sx={{ mt: 0.5, ml: 1.5 }}>
                      Status is required
                    </Typography>
                  )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label='Remark' value={editedData.remark || ''} onChange={(e) => handleFieldChange('remark', e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseEdit} variant='outlined' disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant='contained' disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => { if (!importLoading) setImportDialogOpen(false); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #ddd', py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <UploadFileIcon color="primary" sx={{ fontSize: 28 }} />
              <Typography variant="h6" fontWeight="700">Bulk Import Plots</Typography>
            </Box>
            {!importLoading && (
              <IconButton onClick={() => { setActiveImportStep(0); setImportDialogOpen(false); }} size="small">
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ px: 4, py: 3, borderBottom: '1px solid #eee' }}>
            <Stepper activeStep={activeImportStep} alternativeLabel>
              {['Upload File', 'Review & Validate', 'Import Data'].map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          <Box sx={{ p: 3, minHeight: '300px' }}>
            {activeImportStep === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CloudUploadIcon sx={{ fontSize: 72, color: 'primary.main', mb: 2, opacity: 0.8 }} />
                <Typography variant="h6" fontWeight="600" gutterBottom>Upload Excel File</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '400px', mx: 'auto', mb: 4 }}>
                  Ensure your file includes columns for Plot Name, Width, Length, and Price.
                </Typography>

                <Box sx={{ mb: 4 }}>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadSample}
                    sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}
                  >
                    Download Sample Template
                  </Button>
                </Box>

                <Box
                  sx={{
                    border: '2px dashed #ddd',
                    borderRadius: 3,
                    p: 6,
                    cursor: 'pointer',
                    transition: '0.3s',
                    backgroundColor: '#fafafa',
                    '&:hover': { borderColor: 'primary.main', backgroundColor: '#f0f4ff' }
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    accept=".xlsx, .xls"
                    onChange={handleImportFileSelect}
                  />
                  <Typography variant="subtitle1" fontWeight="bold">
                    {importFileName || 'Click to select or drag Excel file'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Supports .xlsx and .xls formats
                  </Typography>
                </Box>
              </Box>
            )}

            {activeImportStep === 1 && (
              <Box>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ textAlign: 'center', p: 2, bgcolor: '#f8f9fa' }}>
                      <Typography variant="h5" fontWeight="bold">{validationResult?.total || 0}</Typography>
                      <Typography variant="body2" color="text.secondary">Total Records</Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ textAlign: 'center', p: 2, bgcolor: '#e8f5e9', borderColor: '#81c784' }}>
                      <Typography variant="h5" fontWeight="bold" color="success.main">{validationResult?.valid || 0}</Typography>
                      <Typography variant="body2" color="success.main">Valid Records</Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ textAlign: 'center', p: 2, bgcolor: '#fffbed', borderColor: '#ffe082' }}>
                      <Typography variant="h5" fontWeight="bold" color="warning.main">{validationResult?.invalid || 0}</Typography>
                      <Typography variant="body2" color="warning.main">Invalid/Check Needed</Typography>
                    </Card>
                  </Grid>
                </Grid>

                <Typography variant="subtitle2" fontWeight="700" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <VerifiedIcon color="primary" sx={{ fontSize: 20 }} /> Review Data
                </Typography>

                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '300px', borderRadius: 2 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ bgcolor: '#eee', fontWeight: 'bold' }}>Row</TableCell>
                        <TableCell sx={{ bgcolor: '#eee', fontWeight: 'bold' }}>Name</TableCell>
                        <TableCell sx={{ bgcolor: '#eee', fontWeight: 'bold' }}>Price</TableCell>
                        <TableCell sx={{ bgcolor: '#eee', fontWeight: 'bold' }}>Status</TableCell>
                        <TableCell sx={{ bgcolor: '#eee', fontWeight: 'bold' }}>Validity</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {validatedData.map((v, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{v.rowIndex}</TableCell>
                          <TableCell>{v.data['Plot Name *'] || v.data['plotName'] || v.data['Name'] || '-'}</TableCell>
                          <TableCell>{v.data['Price per sqft *'] || v.data['Price'] || '-'}</TableCell>
                          <TableCell>
                            <Chip label={v.isValid ? 'Valid' : 'Error'} size="small" color={v.isValid ? 'success' : 'error'} variant="outlined" />
                          </TableCell>
                          <TableCell>
                            {v.errors.length > 0 ? (
                              <Typography variant="caption" color="error">{v.errors.join(', ')}</Typography>
                            ) : (
                              <ValidIcon color="success" sx={{ fontSize: 18 }} />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {activeImportStep === 2 && (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                {importLoading ? (
                  <>
                    <CircularProgress size={60} sx={{ mb: 3 }} />
                    <Typography variant="h6" fontWeight="600">Importing Data...</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Please do not close this window while we process your plots.
                    </Typography>
                    <Box sx={{ width: '100%', mt: 4, maxWidth: '400px', mx: 'auto' }}>
                      <LinearProgress variant="indeterminate" sx={{ height: 8, borderRadius: 4 }} />
                    </Box>
                  </>
                ) : importSuccess ? (
                  <>
                    <ValidIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                    <Typography variant="h5" fontWeight="bold" color="success.main">Success!</Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>{importSuccess}</Typography>
                  </>
                ) : (
                  <>
                    <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
                    <Typography variant="h5" fontWeight="bold" color="error.main">Import Failed</Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>{importError || 'An unexpected error occurred.'}</Typography>
                  </>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, px: 3, borderTop: '1px solid #eee', bgcolor: '#fbfbfb' }}>
          <Button
            onClick={() => { setActiveImportStep(0); setImportDialogOpen(false); }}
            disabled={importLoading}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          {(activeImportStep === 1) && (
            <Button
              variant="contained"
              onClick={handleImportSubmit}
              disabled={importLoading || validationResult?.valid === 0}
              sx={{ textTransform: 'none', fontWeight: 700, px: 4, borderRadius: 2 }}
            >
              Confirm & Start Import
            </Button>
          )}
          {activeImportStep === 2 && !importLoading && (
            <Button
              variant="contained"
              onClick={() => setActiveImportStep(0)}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
            >
              Try Again
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        onClose={() => setAlertModal({ ...alertModal, open: false })}
      />
    </Box>
  );
};

export default PlotListDetails;
